import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button, Dropdown, Icon, Modal, Popup, Table } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import { countries } from "utils/countries";

import type { CreditsafeSearchCompany } from "components/innointel/types";

const CompanySearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  .input-area {
    display: flex;
    flex-direction: column;
    > span {
      display: block;
      font-size: 12px;
      font-weight: 600;
    }
    &:not(:last-of-type) {
      margin-bottom: 15px;
    }
  }
`;

type CreditsafeManualMatchProps = {
  companyId: string;
  name: string;
  location?: OpenAPI.Schemas["InnointelCompanies"]["location"];
  isOpen?: boolean;
  onClose?: () => void;
  onError?: (errorMessage: string) => void;
};

const CreditsafeManualMatch = ({
  companyId,
  isOpen,
  onClose,
  name: propName,
  location: propLocation,
  onError,
}: CreditsafeManualMatchProps) => {
  const user = useAppSelector((state) => state.user);
  const organisationId = user?.ownerOrganisation?._id;
  const [name, setName] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [initialSearchDone, setInitialSearchDone] = useState<boolean>(false);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState<string>("");
  const [companies, setCompanies] = useState<CreditsafeSearchCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");

  const searchCompanies = useCallback(
    (newName = "", newLocation = "") => {
      if (!organisationId) {
        return;
      }
      setLoadingCompanies(true);
      setSelectedCompany("");
      if (newName) {
        setName(newName);
      }
      if (newLocation) {
        setLocation(newLocation);
      }
      api.verifications.searchForCompanies(
        newLocation || location,
        "",
        newName || name,
        "org",
        organisationId,
        ({ companies: newCompanies }) => {
          setCompanies(newCompanies ?? []);
          setLoadingCompanies(false);
        },
        (err) => {
          toast.error(err.message);
          setLoadingCompanies(false);
          onError?.(err.message);
        },
      );
    },
    [location, name, onError, organisationId],
  );

  const assignCreditsafeInformation = useCallback(() => {
    setLoadingReport(true);
    api.innointel.assignCreditsafeId(
      companyId,
      { creditsafeId: selectedCompany },
      () => {
        api.invalidateQueries(api.innointel.getCompany, [companyId]);
        onClose?.();
        setLoadingReport(false);
      },
      () => {
        onClose?.();
        setLoadingReport(false);
      },
    );
  }, [companyId, selectedCompany, onClose]);

  useEffect(() => {
    if (isOpen && !initialSearchDone) {
      setInitialSearchDone(true);
      let newLocation = "";
      setName(propName);
      if (propLocation?.search?.address?.iso2) {
        const countryIso2 = propLocation.search.address.iso2;
        newLocation = countryIso2;
        setLocation(countryIso2);
      } else if (propLocation?.search?.address?.country) {
        const countryNameLowerCase =
          propLocation.search.address.country.toLowerCase();
        const countryIso2 = countries.find(
          (country) =>
            country.name.toLowerCase().indexOf(countryNameLowerCase) > -1,
        )?.iso2;
        if (countryIso2) {
          newLocation = countryIso2;
          setLocation(countryIso2);
        }
      }
      if (propName && newLocation) {
        searchCompanies(propName, newLocation);
      }
    }
  }, [propName, propLocation, searchCompanies, initialSearchDone, isOpen]);

  const filteredCountries = useMemo(() => {
    const searchTerm = countrySearchTerm.toLowerCase();
    return countries.filter((country) =>
      country.name.toLowerCase().includes(searchTerm),
    );
  }, [countrySearchTerm]);

  const CountryPartialMatchIcon = useMemo(
    () => (
      <Popup
        on="hover"
        content="This piece of information matches the original company"
        trigger={
          <Icon name="check circle" color="green" style={{ marginLeft: 5 }} />
        }
      />
    ),
    [],
  );

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={isOpen}
      onClose={onClose}
    >
      <Modal.Header>
        <p>Find credit information for {propName}</p>
        <p style={{ fontWeight: 400, fontSize: 14 }}>
          We couldn&apos;t find credit information for this company
          automatically - Please search for an option below to link credit
          information.
        </p>
      </Modal.Header>
      <Modal.Content scrolling>
        <CompanySearchContainer>
          <div className="input-area">
            <span>Company name</span>
            <Input
              value={name}
              onChange={(_e, { value }) => {
                setName(value);
              }}
              fluid
            />
          </div>

          <div className="input-area">
            <span>Country</span>
            <Dropdown
              fluid
              selection
              value={location}
              onChange={(_e, { value }) => {
                setLocation(String(value));
                setCountrySearchTerm("");
              }}
              searchQuery={countrySearchTerm}
              search
              onSearchChange={(_e, { searchQuery }) =>
                setCountrySearchTerm(searchQuery)
              }
              options={filteredCountries.map((country) => ({
                key: country.iso2,
                value: country.iso2,
                text: country.name,
              }))}
            />
          </div>

          <div className="input-area">
            <Button
              fluid
              primary={!selectedCompany}
              icon={selectedCompany ? "check" : "search"}
              color={selectedCompany ? "green" : undefined}
              onClick={
                selectedCompany
                  ? () => assignCreditsafeInformation()
                  : () => searchCompanies()
              }
              loading={loadingCompanies || loadingReport}
              disabled={loadingCompanies || loadingReport}
              content={
                selectedCompany
                  ? "Get credit information for this result"
                  : "Search"
              }
            />
          </div>

          {companies.length > 0 ? (
            <Table basic="very" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Company name</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Reg no</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {companies.map((company) => (
                  <Table.Row
                    key={company.id ?? company._id}
                    onClick={() =>
                      setSelectedCompany(
                        selectedCompany === company.id
                          ? ""
                          : (company.id ?? ""),
                      )
                    }
                    style={{ cursor: "pointer" }}
                    active={selectedCompany === company.id}
                  >
                    <Table.Cell>
                      {company.name}
                      {company.name?.toLowerCase() === name.toLowerCase()
                        ? CountryPartialMatchIcon
                        : null}
                    </Table.Cell>
                    <Table.Cell>
                      {company.address?.simpleValue}
                      {company.address?.postCode?.toLowerCase() ===
                      propLocation?.search?.address?.postcode?.toLowerCase()
                        ? CountryPartialMatchIcon
                        : null}
                    </Table.Cell>
                    <Table.Cell>{company.regNo}</Table.Cell>
                    <Table.Cell>
                      {company.statusDescription ?? company.status}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : null}
        </CompanySearchContainer>
      </Modal.Content>
    </Modal>
  );
};

export default CreditsafeManualMatch;
